import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type SurveyKind = 'pulse' | 'enps' | 'custom';
export type SurveyStatus = 'draft' | 'open' | 'closed';
export type QuestionType = 'scale' | 'text' | 'nps';

export interface SurveyQuestion { id: string; prompt: string; qtype: QuestionType; position: number }

export interface SurveyListItem {
  id: string; title: string; description: string | null; kind: SurveyKind; status: SurveyStatus;
  anonymous: boolean; openedAt: string | null; closedAt: string | null; createdAt: string;
  recipientCount: number; respondedCount: number;
}

export interface MySurvey {
  id: string; title: string; description: string | null; kind: SurveyKind; status: SurveyStatus;
  anonymous: boolean; responded: boolean; questions: SurveyQuestion[];
}

export interface SurveyTheme { theme: string; sentiment: 'positive' | 'neutral' | 'negative'; mentionCount: number; summary: string | null }

export interface QuestionResult {
  id: string; prompt: string; qtype: QuestionType; count: number;
  average?: number | null; distribution?: Record<number, number>; enps?: number | null; texts?: string[];
}

export interface SurveyResults {
  id: string; title: string; kind: SurveyKind; status: SurveyStatus; anonymous: boolean;
  recipientCount: number; respondedCount: number; responseRate: number;
  questions: QuestionResult[]; themes: SurveyTheme[];
}

export function useSurveys() {
  return useQuery({
    queryKey: ['surveys'],
    queryFn: async () => (await api.get<{ surveys: SurveyListItem[] }>('/api/surveys')).data.surveys,
  });
}

export function useMySurveys() {
  return useQuery({
    queryKey: ['surveys', 'mine'],
    queryFn: async () => (await api.get<{ surveys: MySurvey[] }>('/api/surveys/mine')).data.surveys,
  });
}

export function useSurveyResults(id: string | null) {
  return useQuery({
    queryKey: ['surveys', 'results', id],
    queryFn: async () => (await api.get<SurveyResults>(`/api/surveys/${id}/results`)).data,
    enabled: !!id,
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['surveys'] });
}

export function useCreateSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { title: string; description?: string; kind: SurveyKind; anonymous?: boolean; questions?: Array<{ prompt: string; qtype: QuestionType }> }) =>
      (await api.post('/api/surveys', input)).data,
    onSuccess: () => invalidate(qc),
  });
}

export function useSetSurveyStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'open' | 'close' }) =>
      (await api.post(`/api/surveys/${id}/${action}`, {})).data,
    onSuccess: () => invalidate(qc),
  });
}

export function useRespondSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, answers }: { id: string; answers: Array<{ questionId: string; score?: number; answerText?: string }> }) =>
      (await api.post(`/api/surveys/${id}/respond`, { answers })).data,
    onSuccess: () => invalidate(qc),
  });
}

export function useAnalyzeSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.post<{ themes: SurveyTheme[] }>(`/api/surveys/${id}/analyze`, {})).data,
    onSuccess: () => invalidate(qc),
  });
}
